import * as THREE from 'three';
import type { MapDefinition, Point } from '../../../shared/game/world-types.js';
import type { CritterGround } from '../critters/critter-ground.js';

/** A puff of dust: a dense core with a soft rim, unlike the smoke sprites' thin falloff, so it reads as a cloud rather than a haze. */
function dustTexture() {
  const size = 32, data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const d = Math.hypot(x - size / 2 + .5, y - size / 2 + .5) / (size / 2), edge = THREE.MathUtils.smoothstep(1 - d, 0, .45), i = (y * size + x) * 4;
    data.set([255, 255, 255, Math.round(255 * edge)], i);
  }
  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat); texture.needsUpdate = true; return texture;
}

export type Surface = 'wet' | 'sand' | 'road' | 'grass';

const PRINTS = 240, RIPPLES = 14, PUFFS = 12;
/** Road dust is a light haze over the road, not a cloud; it starts just above the surface and drifts up as it thins. */
const PUFF_OPACITY = .3, PUFF_LIFT = .3, PUFF_RISE = .45;
/** Wet sand is the first few metres of the beach; sand is as far inland as the ground is painted sand (see `coast-visual.ts`). */
const WET_REACH = 3.5, SAND_REACH = 28, RIPPLE_REACH = 2.6;

const PRINT_VERTEX = `attribute float fade; attribute vec3 tone; varying vec2 vUv; varying float vFade; varying vec3 vTone;
  void main(){ vUv = uv; vFade = fade; vTone = tone; gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.); }`;
// A bare foot: an oval heel and a broader ball with the toes' edge, soft at the rim.
const PRINT_FRAGMENT = `varying vec2 vUv; varying float vFade; varying vec3 vTone;
  float oval(vec2 p, vec2 c, vec2 r){ vec2 q = (p - c) / r; return 1. - smoothstep(.6, 1., length(q)); }
  void main(){
    float heel = oval(vUv, vec2(.5, .28), vec2(.2, .17)), ball = oval(vUv, vec2(.5, .68), vec2(.3, .24));
    float alpha = max(heel, ball) * vFade;
    if (alpha < .01) discard;
    gl_FragColor = vec4(vTone, alpha);
  }`;

/**
 * Where walking leaves a mark: bare footprints in sand (dark and sharp in wet sand, fading over half a minute), a faint dusty
 * print and a puff on the roads, and a ring of ripples where a foot meets the tide line or a tide pool. Grass leaves no print;
 * it bends instead (see `TrampleTrail`). Everything is pooled, so a long walk costs the same as a short one. Purely visual.
 */
export class GroundTraces {
  private readonly prints: THREE.InstancedMesh;
  private readonly fade = new Float32Array(PRINTS);
  private readonly tone = new Float32Array(PRINTS * 3);
  private readonly born = new Float32Array(PRINTS).fill(-1e9);
  private readonly life = new Float32Array(PRINTS).fill(1);
  private readonly strength = new Float32Array(PRINTS);
  private cursor = 0;
  private readonly ripples: { mesh: THREE.Mesh; born: number }[] = [];
  private rippleCursor = 0;
  private readonly puffs: { sprite: THREE.Sprite; born: number; size: number; ground: number }[] = [];
  private puffCursor = 0;
  private readonly pools: { x: number; z: number; reach: number }[];
  private now = 0;
  private readonly dummy = new THREE.Object3D();
  private readonly ring: THREE.BufferGeometry;

  constructor(private scene: THREE.Scene, map: MapDefinition, private ground: CritterGround) {
    const geometry = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    geometry.setAttribute('fade', new THREE.InstancedBufferAttribute(this.fade, 1).setUsage(THREE.DynamicDrawUsage));
    geometry.setAttribute('tone', new THREE.InstancedBufferAttribute(this.tone, 3));
    const material = new THREE.ShaderMaterial({ vertexShader: PRINT_VERTEX, fragmentShader: PRINT_FRAGMENT, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
    this.prints = new THREE.InstancedMesh(geometry, material, PRINTS); this.prints.frustumCulled = false; this.prints.renderOrder = 1;
    // Every slot starts collapsed to nothing.
    this.dummy.scale.setScalar(0); this.dummy.updateMatrix();
    for (let i = 0; i < PRINTS; i++) this.prints.setMatrixAt(i, this.dummy.matrix);
    scene.add(this.prints);

    const ring = this.ring = new THREE.RingGeometry(.28, .36, 24).rotateX(-Math.PI / 2);
    for (let i = 0; i < RIPPLES; i++) {
      const mesh = new THREE.Mesh(ring, new THREE.MeshBasicMaterial({ color: 0xf2f6f4, transparent: true, opacity: 0, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -3, polygonOffsetUnits: -3 }));
      mesh.visible = false; mesh.renderOrder = 2; mesh.frustumCulled = false; scene.add(mesh); this.ripples.push({ mesh, born: -1e9 });
    }
    const puff = dustTexture();
    for (let i = 0; i < PUFFS; i++) {
      const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: puff, color: 0xb9a780, transparent: true, depthWrite: false, opacity: 0 }));
      // Drawn after the transparent road surface (renderOrder 1) and the prints and ripples on it, or the road paints over the dust.
      sprite.visible = false; sprite.renderOrder = 3; scene.add(sprite); this.puffs.push({ sprite, born: -1e9, size: .3, ground: 0 });
    }
    this.pools = (map.scenery?.props ?? []).filter(prop => prop.kind === 'tide-pool').map(prop => ({ x: prop.x, z: prop.z, reach: 1.7 * (prop.scale ?? 1) }));
  }

  /** What the ground at a point is made of, for what a foot leaves on it. Matches how the ground is painted. */
  surface(x: number, z: number): Surface {
    const shore = this.ground.shoreDistance(x, z);
    if (shore < WET_REACH) return 'wet';
    if (this.ground.onRoad(x, z) || this.ground.onTrail(x, z)) return 'road';
    return shore < SAND_REACH || this.ground.biome(x, z) === 'sand' ? 'sand' : 'grass';
  }

  /** One footfall at `at`, heading `heading`, with the left (`-1`) or right (`1`) foot. */
  step(at: Point, heading: number, foot: -1 | 1, running = false) {
    const forward = { x: Math.sin(heading), z: Math.cos(heading) }, x = at.x + Math.cos(heading) * foot * .13 + forward.x * .08, z = at.z - Math.sin(heading) * foot * .13 + forward.z * .08;
    const surface = this.surface(at.x, at.z);
    if (surface === 'wet') this.print(x, z, heading, [.3, .24, .17], .5, 30);
    else if (surface === 'sand') this.print(x, z, heading, [.5, .4, .28], .3, 36);
    else if (surface === 'road') { this.print(x, z, heading, [.36, .28, .2], .3, 14); this.dust(x, z, running ? 1.2 : .9); }
    const pool = this.pools.some(p => Math.hypot(p.x - at.x, p.z - at.z) < p.reach);
    if (pool || (this.ground.shoreDistance(at.x, at.z) < RIPPLE_REACH && this.ground.shoreDistance(at.x, at.z) > 0)) this.ripple(x, z);
  }

  private print(x: number, z: number, heading: number, tone: [number, number, number], strength: number, life: number) {
    const i = this.cursor++ % PRINTS;
    this.dummy.position.set(x, this.ground.height(x, z) + .045, z); this.dummy.rotation.set(0, heading + Math.PI, 0); this.dummy.scale.set(.27, 1, .42); this.dummy.updateMatrix();
    const slope = this.ground.gradient(x, z);
    this.dummy.quaternion.premultiply(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(-slope.x, 1, -slope.z).normalize()));
    this.dummy.updateMatrix();
    this.prints.setMatrixAt(i, this.dummy.matrix); this.prints.instanceMatrix.needsUpdate = true;
    this.tone.set(tone, i * 3); (this.prints.geometry.getAttribute('tone') as THREE.BufferAttribute).needsUpdate = true;
    this.born[i] = this.now; this.life[i] = life; this.strength[i] = strength; this.fade[i] = strength;
  }

  private ripple(x: number, z: number) {
    const entry = this.ripples[this.rippleCursor++ % RIPPLES];
    entry.born = this.now; entry.mesh.position.set(x, this.ground.height(x, z) + .07, z); entry.mesh.visible = true;
  }

  private dust(x: number, z: number, size: number) {
    const entry = this.puffs[this.puffCursor++ % PUFFS];
    entry.born = this.now; entry.size = size; entry.ground = this.ground.height(x, z);
    entry.sprite.position.set(x, entry.ground + PUFF_LIFT, z); entry.sprite.scale.setScalar(size); entry.sprite.visible = true;
  }

  /** `seconds` is any steadily increasing clock; prints, ripples and puffs age by it. */
  update(seconds: number) {
    this.now = seconds;
    let changed = false;
    for (let i = 0; i < PRINTS; i++) {
      const age = seconds - this.born[i];
      if (age < 0 || age > this.life[i]) { if (this.fade[i] !== 0) { this.fade[i] = 0; changed = true; } continue; }
      this.fade[i] = this.strength[i] * (1 - age / this.life[i]) ** 1.4; changed = true;
    }
    if (changed) (this.prints.geometry.getAttribute('fade') as THREE.BufferAttribute).needsUpdate = true;
    for (const entry of this.ripples) {
      const age = seconds - entry.born;
      if (age > .9) { entry.mesh.visible = false; continue; }
      entry.mesh.scale.setScalar(1 + age * 3.2); (entry.mesh.material as THREE.MeshBasicMaterial).opacity = .55 * (1 - age / .9) ** 1.5;
    }
    for (const entry of this.puffs) {
      const age = seconds - entry.born;
      if (age > 1.1) { entry.sprite.visible = false; continue; }
      entry.sprite.position.y = entry.ground + PUFF_LIFT + age * PUFF_RISE; entry.sprite.scale.setScalar(entry.size * (1 + age * 1.8)); (entry.sprite.material as THREE.SpriteMaterial).opacity = PUFF_OPACITY * Math.sin(Math.min(1, age / 1.1) * Math.PI) ** .7;
    }
  }

  /** How many prints and ripples are showing, for tests and diagnostics. */
  get showing() { return { prints: this.fade.filter(value => value > .01).length, ripples: this.ripples.filter(entry => entry.mesh.visible).length, puffs: this.puffs.filter(entry => entry.sprite.visible).length }; }

  dispose() {
    this.ring.dispose(); this.prints.removeFromParent(); this.prints.geometry.dispose(); (this.prints.material as THREE.Material).dispose();
    for (const { mesh } of this.ripples) { mesh.removeFromParent(); (mesh.material as THREE.Material).dispose(); }
    for (const { sprite } of this.puffs) { sprite.removeFromParent(); sprite.material.dispose(); }
  }
}
